import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { INGREDIENT_CATEGORIES } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { api, queryKeys } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { CATEGORY_LABEL, categoryLabel } from '../../lib/ingredientDisplay';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const CATEGORY_FILTERS = [
  { value: '', label: 'All categories' },
  ...INGREDIENT_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABEL[c] })),
];

/** Search-driven browser of the shared ingredient vocabulary. */
export function IngredientsPage() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const debouncedQ = useDebouncedValue(q.trim(), 250);

  const search = useQuery({
    queryKey: queryKeys.ingredientSearch(debouncedQ),
    queryFn: () => api.ingredients.search(debouncedQ, 50),
    enabled: debouncedQ.length > 0,
  });

  const items = (search.data?.items ?? []).filter((i) => !category || i.category === category);

  return (
    <AppShell title="Ingredients">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          The shared ingredient catalogue used across recipes, pantry and grocery lists.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="ingredients-search" className="text-sm font-medium text-ink">
              Search
            </label>
            <input
              id="ingredients-search"
              type="search"
              value={q}
              placeholder="e.g. garlic"
              onChange={(e) => setQ(e.target.value)}
              className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="ingredients-category" className="text-sm font-medium text-ink">
              Category
            </label>
            <select
              id="ingredients-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
            >
              {CATEGORY_FILTERS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {debouncedQ.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line bg-surface p-6 text-center text-sm text-muted">
            Search for an ingredient to get started.
          </p>
        ) : null}
        {search.isError ? <Alert tone="error">{messageForError(search.error)}</Alert> : null}
        {search.isSuccess && items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-line bg-surface p-6 text-center text-sm text-muted">
            No ingredients match “{debouncedQ}”.
          </p>
        ) : null}

        <ul className="flex flex-col gap-2">
          {items.map((ing) => (
            <li key={ing.id}>
              <Link
                to={`/ingredients/${ing.id}`}
                className="flex flex-col gap-0.5 card p-3 hover:border-accent"
              >
                <span className="font-medium text-ink">{ing.canonicalName}</span>
                <span className="text-xs text-muted">
                  {categoryLabel(ing.category)} · default unit {ing.defaultUnit}
                  {ing.matchedAlias ? ` · matches “${ing.matchedAlias}”` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </AppShell>
  );
}
